import { Viewport } from './viewport.js';
import { LODManager } from './lodManager.js';
import { WireframeManager } from './wireframeManager.js';
import { UIController } from './ui.js';

/**
 * Main Application Orchestrator.
 */

export class App {
  constructor() {
    this.modelsConfig = [];
    this.currentModelConfig = null;
    this.currentLodLevel = 0;
    this.viewingMode = 'single'; // 'single', 'split', 'distance'

    this.distanceThresholds = []; // Threshold distances per LOD level
    this.isDraggingSlider = false;

    // Subsystems
    this.viewport = new Viewport(document.getElementById('threeCanvas'));
    
    this.lodManager = new LODManager();
    this.wireframeManager = new WireframeManager();
    this.ui = new UIController(this);

    this.init();
  }

  async init() {
    try {
      const resp = await fetch('/models/config.json');
      const data = await resp.json();
      this.modelsConfig = data.models || [];
    } catch (err) {
      console.warn('Could not fetch /models/config.json, using built-in defaults.', err);
      this.modelsConfig = [
        { id: 'cyber_mech', name: 'Cyber Mech Unit', type: 'procedural', lodCount: 6, distances: [0, 6, 12, 20, 32, 48], description: 'Hard-surface character with 6 LOD levels.' },
        { id: 'sci_fi_ship', name: 'Interceptor Craft', type: 'procedural', lodCount: 8, distances: [0, 5, 10, 16, 24, 34, 46, 60], description: 'Aerospace fighter with 8 decimation levels.' },
        { id: 'ancient_statue', name: 'Sculpted Bust', type: 'procedural', lodCount: 5, distances: [0, 7, 15, 25, 40], description: 'Organic sculpt mesh decimation showcase.' },
        { id: 'custom_single_glb', name: 'Single GLB Container', type: 'single-file', file: 'models/sample_single_lod.glb', distances: [0, 8, 16, 26, 38, 52], description: 'Single GLB containing LOD0..LOD5 nodes.' }
      ];
    }

    this.ui.populateModelDropdown(this.modelsConfig);

    if (this.modelsConfig.length > 0) {
      await this.selectModel(this.modelsConfig[0].id);
    }

    this.setupCameraDistanceListener();
    this.viewport.onWindowResize();
    this.startRenderLoop();
  }

  async selectModel(modelId) {
    const config = this.modelsConfig.find(m => m.id === modelId) || this.modelsConfig[0];
    this.currentModelConfig = config;

    this.ui.updateModelHeaderInfo(config);

    const availableLevels = await this.lodManager.loadModel(config);
    this.currentLodLevel = availableLevels[0] || 0;

    // Dynamically scan model texture maps and update inspector options
    const detectedTextureModes = this.lodManager.getDetectedTextureModes();
    this.ui.updateInspectorDropdownOptions(detectedTextureModes);

    // Load or calculate distance thresholds
    this.setupDistanceThresholds(config, availableLevels);

    this.ui.renderLodButtons(availableLevels, this.currentLodLevel);
    this.ui.renderDistanceInputs(availableLevels, this.distanceThresholds, this.currentLodLevel);

    // Auto-fit camera to bounding box with fresh viewport bounds
    this.viewport.onWindowResize();
    this.viewport.fitCameraToBoundingBox(this.lodManager.modelSize, this.lodManager.modelRadius);

    // Precompile all LOD geometries & shaders to guarantee buttery smooth transitions
    this.viewport.precompileLODs(this.lodManager.lodMap, availableLevels);

    this.updateActiveMeshView();
  }

  setupDistanceThresholds(config, availableLevels) {
    if (config.distances && Array.isArray(config.distances) && config.distances.length === availableLevels.length) {
      this.distanceThresholds = [...config.distances];
    } else {
      // Default intervals based on model radius
      const r = this.lodManager.modelRadius || 2.0;
      this.distanceThresholds = availableLevels.map((lvl, idx) => parseFloat((idx * r * 2.5).toFixed(1)));
    }
  }

  selectLodLevel(level) {
    this.currentLodLevel = level;
    this.updateActiveMeshView();
  }

  updateActiveMeshView() {
    const activeMeshNode = this.lodManager.getLodMesh(this.currentLodLevel);
    const lod0MeshNode = this.lodManager.getLodMesh(0);

    // Keep source nodes clean from wireframe segments before split clone
    this.wireframeManager.clearWireframe(lod0MeshNode);
    this.wireframeManager.clearWireframe(activeMeshNode);

    this.viewport.setModelMesh(activeMeshNode);
    this.wireframeManager.applyWireframeToModel(activeMeshNode, this.viewport.scene);

    // Telemetry updates
    const telemetry = this.lodManager.getLodTelemetry(this.currentLodLevel);
    this.ui.updateTelemetry(telemetry);
  }

  setViewingMode(modeKey) {
    this.viewingMode = modeKey;

    const singleContainer = document.getElementById('singleViewport');

    if (modeKey === 'distance') {
      if (singleContainer) singleContainer.classList.add('active');
      this.ui.toggleDistanceHud(true);
      this.viewport.onWindowResize();
      
      const dist = this.viewport.camera.position.distanceTo(this.viewport.controls.target);
      this.ui.updateSliderValue(dist);
      this.evaluateAutoDistanceLod(dist);
      this.updateActiveMeshView();
    } else {
      // Single View
      if (singleContainer) singleContainer.classList.add('active');
      this.ui.toggleDistanceHud(false);
      this.viewport.onWindowResize();
      this.updateActiveMeshView();
    }
  }

  setupCameraDistanceListener() {
    this.viewport.controls.addEventListener('change', () => {
      if (this.viewingMode === 'distance' && !this.isDraggingSlider) {
        const dist = this.viewport.camera.position.distanceTo(this.viewport.controls.target);
        this.ui.updateSliderValue(dist);
        this.evaluateAutoDistanceLod(dist);
      }
    });
  }

  onDistanceSliderChange(dist) {
    this.isDraggingSlider = true;
    
    this.viewport.setCameraDistance(dist);
    this.evaluateAutoDistanceLod(dist);

    setTimeout(() => {
      this.isDraggingSlider = false;
    }, 50);
  }

  evaluateAutoDistanceLod(dist) {
    const levels = this.lodManager.getAvailableLodLevels();
    let selectedLevel = levels[0];

    for (let i = levels.length - 1; i >= 0; i--) {
      const threshold = this.distanceThresholds[i] !== undefined ? this.distanceThresholds[i] : i * 8.0;
      if (dist >= threshold) {
        selectedLevel = levels[i];
        break;
      }
    }

    if (selectedLevel !== this.currentLodLevel) {
      this.selectLodLevel(selectedLevel);
      this.ui.setActiveLodButton(selectedLevel);
    }
  }

  updateDistanceThreshold(index, value) {
    if (this.distanceThresholds[index] !== undefined) {
      this.distanceThresholds[index] = value;
      this.ui.updateDistanceSliderBounds(this.distanceThresholds);
      const dist = this.viewport.camera.position.distanceTo(this.viewport.controls.target);
      this.evaluateAutoDistanceLod(dist);
    }
  }

  resetDistanceThresholds() {
    const availableLevels = this.lodManager.getAvailableLodLevels();
    this.setupDistanceThresholds(this.currentModelConfig, availableLevels);
    this.ui.renderDistanceInputs(availableLevels, this.distanceThresholds, this.currentLodLevel);
  }

  toggleWireframeOverlay(enabled) {
    this.wireframeManager.setOverlayMode(enabled);
    this.updateActiveMeshView();
  }

  setWireframeColor(hexColor) {
    this.wireframeManager.setWireframeColor(hexColor);
    this.updateActiveMeshView();
  }

  setShadingMode(shadingKey) {
    this.lodManager.applyShadingMode(shadingKey);
    
    // Precompile newly generated inspector materials
    const levels = this.lodManager.getAvailableLodLevels();
    this.viewport.precompileLODs(this.lodManager.lodMap, levels);
    
    this.updateActiveMeshView();
  }

  setLightingPreset(presetKey) {
    this.viewport.setLightingPreset(presetKey);

    // Precompile PBR shaders with the new environment reflection map
    const levels = this.lodManager.getAvailableLodLevels();
    this.viewport.precompileLODs(this.lodManager.lodMap, levels);
  }

  setExposure(val) {
    this.viewport.setExposure(val);
  }

  setEmissiveIntensity(val) {
    this.lodManager.setEmissiveIntensity(val);
    this.updateActiveMeshView();
  }

  setGridVisible(visible) {
    this.viewport.setGridVisible(visible);
  }

  setAutoRotate(enabled) {
    this.viewport.setAutoRotate(enabled);
  }

  setAutoRotateLight(enabled) {
    this.viewport.setAutoRotateLight(enabled);
  }

  setLightFollowsCamera(enabled) {
    this.viewport.setLightFollowsCamera(enabled);
  }

  resetCamera() {
    this.viewport.fitCameraToBoundingBox(this.lodManager.modelSize, this.lodManager.modelRadius);
  }

  startRenderLoop() {
    const animate = () => {
      requestAnimationFrame(animate);
      this.viewport.render();
    };
    animate();
  }
}

// Bootstrap on DOM load
window.addEventListener('DOMContentLoaded', () => {
  new App();
});
